/**
 * Stock drill-down hrefs for Chart Terminal and Technical Analysis.
 *
 * Same write rule as `writtenNavSearch` (tax only when industry) plus the
 * chart-local `range` so a hop does not reset the window the user was reading.
 */
import {
  MA_OVERLAY_OFF,
  formatCompareSet,
  type ChartSearchParams,
  type StockSearchParams,
  type Ticker,
} from '@meridian/contracts';

import { hrefSearch, writtenNavSearch, type WrittenNavSearch } from './search';

export interface ChartStudyHrefs {
  readonly stock: string;
  readonly chart: string;
  readonly technical: string;
}

export type ChartStudySearch = Pick<
  StockSearchParams,
  'win' | 'idx' | 'range' | 'tax' | 'area' | 'book' | 'cmp'
>;

/** Tax write rule plus optional chart-local `range`. stockTarget omits range. */
export type WrittenChartStudySearch = WrittenNavSearch & {
  readonly range?: StockSearchParams['range'];
};

export function chartStudySearch(search: ChartStudySearch): WrittenChartStudySearch {
  const written = writtenNavSearch(search);
  return search.range === undefined ? written : { ...written, range: search.range };
}

export type ChartSearchWrite = WrittenChartStudySearch & {
  readonly ma?: typeof MA_OVERLAY_OFF;
};

/** Drop `ma` when overlays are on so a chart write cannot pin the omitted default. */
export function chartSearchWrite(parsed: ChartSearchParams): ChartSearchWrite {
  const written = chartStudySearch(parsed);
  return parsed.ma === MA_OVERLAY_OFF ? { ...written, ma: MA_OVERLAY_OFF } : written;
}

function studyHrefQuery(search: ChartStudySearch): Readonly<Record<string, string | undefined>> {
  const written = chartStudySearch(search);
  return {
    win: written.win,
    idx: written.idx,
    ...(written.range === undefined ? {} : { range: written.range }),
    ...(written.tax === undefined ? {} : { tax: written.tax }),
    ...(written.area === undefined ? {} : { area: written.area }),
    ...(written.book === undefined ? {} : { book: String(written.book) }),
    ...(written.cmp === undefined ? {} : { cmp: formatCompareSet(written.cmp) }),
  };
}

export function chartStudyHrefs(ticker: Ticker, search: ChartStudySearch): ChartStudyHrefs {
  const encoded = encodeURIComponent(ticker);
  const query = studyHrefQuery(search);
  return {
    stock: hrefSearch(`/stock/${encoded}`, query),
    chart: hrefSearch(`/chart/${encoded}`, query),
    technical: hrefSearch(`/technical/${encoded}`, query),
  };
}
